/**
 * GoldenPath — the "Complete Transaction" showcase.
 *
 * Renders the master Golden Path reference EXACTLY: a 3×2 grid of six
 * stage cards (Offer Accepted → AML+Documents → Solicitor Coordination
 * → Mortgage Progress → Exchange Coordination → Ready for Exchange),
 * a back-bar header, and a "What Makes This Powerful" footer strip.
 *
 * Lives inside the existing Deal Room tab system so it inherits the
 * permanent dark glowing sidebar shell. Light-mode surface throughout —
 * matches the operational hierarchy of the reference image.
 *
 * Data is intentionally static and realistic — this is a showcase /
 * narrative surface, not a live data view. The wider operational app
 * still lives in the Flow / Milestones / Documents / Tasks tabs.
 */
import React from "react";
import {
  ArrowLeft, ArrowRight, Bell, MoreHorizontal, Share2, Check, Download,
  TrendingUp, Eye, ShieldCheck, AlertTriangle, FileText, Smartphone,
  Handshake, Paperclip, Plus,
} from "lucide-react";

/* ── Shared design tokens ────────────────────────────────────────────── */
const T = {
  cardBg: "#FFFFFF",
  cardBorder: "rgba(15,23,42,0.06)",
  cardShadow: "0 24px 50px -28px rgba(15,23,42,0.18), 0 6px 14px -8px rgba(15,23,42,0.06)",
  innerBorder: "rgba(15,23,42,0.06)",
  textPrimary: "#0F172A",
  textMuted: "#64748B",
  textSubtle: "#94A3B8",
  violet: "#7C3AED",
  violetSoft: "#F5F3FF",
  violetBorder: "#E9D5FF",
  green: "#10B981",
  greenSoft: "#ECFDF5",
  greenBorder: "#A7F3D0",
  amber: "#F59E0B",
  amberSoft: "#FFFBEB",
  amberBorder: "#FDE68A",
  rose: "#EF4444",
  roseSoft: "#FEF2F2",
  roseBorder: "#FECACA",
  surfaceTint: "linear-gradient(180deg, #FAFAFD 0%, #F4F2FB 100%)",
};

const STATE = {
  complete:    { bg: T.greenSoft,  fg: "#047857", border: T.greenBorder,  label: "✓ COMPLETE" },
  in_progress: { bg: T.violetSoft, fg: "#6D28D9", border: T.violetBorder, label: "IN PROGRESS" },
  at_risk:     { bg: T.amberSoft,  fg: "#B45309", border: T.amberBorder,  label: "⚠ AT RISK" },
  ready:       { bg: T.greenSoft,  fg: "#047857", border: T.greenBorder,  label: "✓ READY" },
};

/* ── Reusable primitives ─────────────────────────────────────────────── */
const StageNumber = ({ n }) => (
  <span
    className="w-9 h-9 rounded-full inline-flex items-center justify-center text-[14px] font-bold text-white shrink-0"
    style={{
      background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
      boxShadow: "0 6px 14px -6px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.22)",
    }}
  >
    {n}
  </span>
);

const StatePill = ({ kind }) => {
  const s = STATE[kind];
  return (
    <span
      className="inline-flex items-center px-2.5 h-[24px] rounded-full text-[10px] font-bold uppercase tracking-[0.10em] whitespace-nowrap"
      style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
    >
      {s.label}
    </span>
  );
};

const TabCount = ({ label, active = false, count }) => (
  <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold whitespace-nowrap relative pb-2"
        style={{ color: active ? T.violet : T.textMuted }}>
    {label}
    {count != null && (
      <span
        className="text-[9.5px] font-bold px-1.5 h-[16px] inline-flex items-center rounded-full tabular-nums"
        style={
          active
            ? { background: T.violetSoft, color: T.violet, border: `1px solid ${T.violetBorder}` }
            : { background: "#F1F5F9", color: "#475569" }
        }
      >
        {count}
      </span>
    )}
    {active && (
      <span aria-hidden="true" className="absolute left-0 right-0 -bottom-[1px] h-[2px] rounded-full"
            style={{ background: T.violet }} />
    )}
  </span>
);

/* Property strip — shared across all 6 cards. */
const PropertyStrip = () => (
  <div
    className="flex items-center gap-3 rounded-xl p-3"
    style={{ background: "#FAFBFD", border: `1px solid ${T.innerBorder}` }}
  >
    <span
      className="w-12 h-12 rounded-lg shrink-0"
      style={{
        background: "linear-gradient(135deg, #C7D2FE 0%, #FBCFE8 100%)",
        backgroundImage: "linear-gradient(135deg, #93C5FD 0%, #C4B5FD 50%, #FBCFE8 100%)",
      }}
      aria-hidden="true"
    />
    <div className="min-w-0 flex-1">
      <p className="text-[12.5px] font-bold leading-tight" style={{ color: T.textPrimary }}>
        12 Greenway Drive, <span className="font-semibold" style={{ color: T.textMuted }}>Chester, CH2 3QL</span>
      </p>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 text-[10.5px]" style={{ color: T.textMuted }}>
        <span><b className="uppercase tracking-[0.10em]" style={{ color: T.textSubtle }}>SELLER</b> &nbsp;<span style={{ color: T.textPrimary }}>Emma Johnson</span></span>
        <span><b className="uppercase tracking-[0.10em]" style={{ color: T.textSubtle }}>BUYER</b> &nbsp;<span style={{ color: T.textPrimary }}>James & Sophie Carter</span></span>
      </div>
    </div>
    <div className="text-right shrink-0 hidden sm:block">
      <span className="inline-flex items-center px-2 h-[20px] rounded-md text-[9.5px] font-bold uppercase tracking-[0.10em]"
            style={{ background: T.violetSoft, color: T.violet, border: `1px solid ${T.violetBorder}` }}>
        Sales
      </span>
      <p className="text-[12.5px] font-bold mt-1.5 tabular-nums" style={{ color: T.textPrimary }}>£425,000</p>
    </div>
  </div>
);

const StageCard = ({ n, title, sub, state, tabs, caption, children }) => (
  <article
    className="rounded-2xl flex flex-col"
    style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow }}
    data-testid={`gp-stage-${n}`}
  >
    <header className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <StageNumber n={n} />
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold leading-tight" style={{ color: T.textPrimary }}>{title}</h3>
            <p className="text-[11.5px] mt-1 leading-snug" style={{ color: T.textMuted }}>{sub}</p>
          </div>
        </div>
        <StatePill kind={state} />
      </div>
      <div className="mt-4">
        <PropertyStrip />
      </div>
      <div className="mt-4 flex items-center gap-5 flex-wrap"
           style={{ borderBottom: `1px solid ${T.innerBorder}` }}>
        {tabs.map((t, i) => (
          <TabCount key={i} label={t.label} count={t.count} active={t.active} />
        ))}
      </div>
    </header>

    <div className="px-5 pb-5 flex-1">{children}</div>

    {caption && (
      <footer className="px-5 py-3.5 text-center text-[11px] italic"
              style={{ color: T.textMuted, borderTop: `1px solid ${T.innerBorder}`, background: "#FAFBFD" }}>
        {caption}
      </footer>
    )}
  </article>
);

/* ── Stage 1 · Offer Accepted ────────────────────────────────────────── */
const Avatar = ({ initials, color = "#C4B5FD", online }) => (
  <span className="relative inline-flex shrink-0">
    <span
      className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-slate-800"
      style={{ background: color }}
    >
      {initials}
    </span>
    {online && (
      <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full"
            style={{ background: T.green, border: "1.5px solid #FFFFFF" }} />
    )}
  </span>
);

const TimelineItem = ({ state = "completed", title, sub, badge }) => {
  const dotColor = state === "completed" ? T.green : state === "active" ? T.violet : "#CBD5E1";
  return (
    <li className="flex items-start gap-3 py-1.5">
      <span className="relative inline-flex items-center justify-center w-5 h-5 shrink-0 mt-0.5">
        {state === "active" && (
          <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: dotColor }} />
        )}
        <span
          className="w-4 h-4 rounded-full inline-flex items-center justify-center"
          style={{ background: state === "completed" ? T.green : "#FFFFFF", border: `1.5px solid ${dotColor}` }}
        >
          {state === "completed" && <Check size={9} className="text-white" strokeWidth={3.4} />}
          {state === "active" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11.5px] font-bold leading-tight" style={{ color: T.textPrimary }}>{title}</p>
          {badge && (
            <span className="text-[9px] font-bold uppercase tracking-[0.10em] px-1.5 h-[16px] inline-flex items-center rounded-full"
                  style={{ background: T.violetSoft, color: T.violet, border: `1px solid ${T.violetBorder}` }}>
              {badge}
            </span>
          )}
        </div>
        <p className="text-[10px] mt-0.5" style={{ color: T.textSubtle }}>{sub}</p>
      </div>
    </li>
  );
};

const ParticipantRow = ({ name, role, color, online }) => (
  <li className="flex items-center gap-2.5 py-1">
    <Avatar initials={name.split(" ").map((p) => p[0]).join("").slice(0, 2)} color={color} online={online} />
    <div className="min-w-0">
      <p className="text-[11px] font-bold leading-tight" style={{ color: T.textPrimary }}>{name}</p>
      <p className="text-[9.5px]" style={{ color: T.textMuted }}>{role}</p>
    </div>
  </li>
);

const Stage1 = () => (
  <div className="space-y-4">
    <ul className="space-y-0.5">
      <TimelineItem title="Offer Accepted" sub="Today · 10:24" />
      <TimelineItem title="Deal Room Created" sub="Today · 10:24" />
      <TimelineItem title="Parties Invited" sub="Today · 10:25" />
      <TimelineItem state="active" title="Awaiting AML from Buyer" sub="" badge="In Progress" />
      <TimelineItem state="active" title="Awaiting TA6 from Seller" sub="" badge="In Progress" />
      <TimelineItem state="pending" title="Instruct Solicitors" sub="" badge="Pending" />
    </ul>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Participants</p>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0">
        <ParticipantRow name="Jack Richmond" role="Agent"          color="#FBBF24" online />
        <ParticipantRow name="James Carter"  role="Buyer"          color="#A5B4FC" online />
        <ParticipantRow name="Sophie Carter" role="Buyer"          color="#FBCFE8" />
        <ParticipantRow name="Emma Johnson"  role="Seller"         color="#FED7AA" online />
        <ParticipantRow name="Buyer Solicitor"  role="TBC"         color="#E2E8F0" />
        <ParticipantRow name="Seller Solicitor" role="TBC"         color="#E2E8F0" />
      </ul>
    </div>
  </div>
);

/* ── Stage 2 · AML + Documents ───────────────────────────────────────── */
const DocRow = ({ doc, from, status }) => {
  const palette =
    status === "UPLOADED" ? { bg: T.greenSoft, fg: "#047857", border: T.greenBorder }
  : status === "AWAITING" ? { bg: T.amberSoft, fg: "#B45309", border: T.amberBorder }
  : status === "IN PROGRESS" ? { bg: T.violetSoft, fg: T.violet, border: T.violetBorder }
  : { bg: "#F1F5F9", fg: "#475569", border: "#E2E8F0" };
  return (
    <tr style={{ borderTop: `1px solid ${T.innerBorder}` }}>
      <td className="py-2 pr-2 text-[11px] font-semibold inline-flex items-center gap-1.5" style={{ color: T.textPrimary }}>
        <FileText size={11} className="text-slate-400" />
        {doc}
      </td>
      <td className="py-2 px-2 text-[10.5px]" style={{ color: T.textMuted }}>{from}</td>
      <td className="py-2 px-2">
        <span className="text-[9px] font-bold uppercase tracking-[0.10em] px-1.5 h-[18px] inline-flex items-center rounded-full"
              style={{ background: palette.bg, color: palette.fg, border: `1px solid ${palette.border}` }}>
          {status}
        </span>
      </td>
      <td className="py-2 pl-2 text-right text-[10px]" style={{ color: T.textMuted }}>
        {status === "UPLOADED" ? "Today" : "—"}
      </td>
    </tr>
  );
};

const ActivityRow = ({ initials, color, who, action, at }) => (
  <li className="flex items-start gap-2.5 py-1.5">
    <Avatar initials={initials} color={color} />
    <div className="min-w-0">
      <p className="text-[11px] leading-tight" style={{ color: T.textPrimary }}>
        <b>{who}</b> <span style={{ color: T.textMuted }}>{action}</span>
      </p>
      <p className="text-[9.5px] mt-0.5" style={{ color: T.textSubtle }}>{at}</p>
    </div>
  </li>
);

const Stage2 = () => (
  <div className="space-y-4">
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Required documents</p>
      <table className="w-full">
        <thead>
          <tr className="text-[9.5px] font-bold uppercase tracking-[0.10em]" style={{ color: T.textSubtle }}>
            <th className="text-left py-1.5 pr-2 font-bold">Document</th>
            <th className="text-left py-1.5 px-2 font-bold">From</th>
            <th className="text-left py-1.5 px-2 font-bold">Status</th>
            <th className="text-right py-1.5 pl-2 font-bold">Uploaded</th>
          </tr>
        </thead>
        <tbody>
          <DocRow doc="ID (Photo)"        from="Buyer"       status="UPLOADED" />
          <DocRow doc="Proof of Address"  from="Buyer"       status="UPLOADED" />
          <DocRow doc="Proof of Funds"    from="Buyer"       status="UPLOADED" />
          <DocRow doc="TA6 (Property Info)" from="Seller"    status="UPLOADED" />
          <DocRow doc="TA10 (Fittings & Contents)" from="Seller" status="AWAITING" />
          <DocRow doc="AML Check"         from="Both Parties" status="IN PROGRESS" />
          <DocRow doc="Authority to Act"  from="Both Parties" status="AWAITING" />
        </tbody>
      </table>
    </div>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Activity</p>
      <ul className="space-y-0">
        <ActivityRow initials="JC" color="#A5B4FC" who="James Carter"  action="uploaded Proof of Funds" at="Today · 10:52" />
        <ActivityRow initials="EJ" color="#FED7AA" who="Emma Johnson"  action="uploaded TA6"            at="Today · 11:03" />
        <ActivityRow initials="JR" color="#FBBF24" who="Jack Richmond" action="requested TA10"          at="Today · 11:04" />
        <ActivityRow initials="TB" color="#86EFAC" who="Tom Bradley"   action="started AML check"       at="Today · 11:10" />
      </ul>
    </div>
  </div>
);

/* ── Stage 3 · Solicitor Coordination ────────────────────────────────── */
const ChatMessage = ({ name, role, time, body, attachment, system }) => {
  if (system) {
    return (
      <div className="rounded-lg px-3 py-2 my-2"
           style={{ background: "#EEF2FF", border: `1px solid ${T.violetBorder}` }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: T.violet }}>
          System update <span className="text-slate-400 font-medium normal-case tracking-normal ml-1">{time}</span>
        </p>
        <p className="text-[11px] mt-1" style={{ color: T.textPrimary }}>{body}</p>
      </div>
    );
  }
  const initials = name.split(" ").map((p) => p[0]).join("").slice(0, 2);
  return (
    <div className="flex items-start gap-2.5 my-2.5">
      <Avatar initials={initials} color="#C4B5FD" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-bold" style={{ color: T.textPrimary }}>{name}</span>
          <span className="text-[9.5px]" style={{ color: T.textSubtle }}>{role}</span>
          <span className="text-[9.5px] ml-auto" style={{ color: T.textSubtle }}>{time}</span>
        </div>
        <p className="text-[11px] mt-1" style={{ color: T.textPrimary }}>{body}</p>
        {attachment && (
          <div className="mt-1.5 inline-flex items-center gap-2 rounded-md px-2.5 py-1.5"
               style={{ background: "#FAFBFD", border: `1px solid ${T.innerBorder}` }}>
            <FileText size={11} className="text-slate-400" />
            <span className="text-[10.5px] font-semibold" style={{ color: T.textPrimary }}>{attachment.name}</span>
            <span className="text-[9.5px]" style={{ color: T.textSubtle }}>{attachment.size}</span>
            <button type="button" className="ml-1 text-slate-400 hover:text-slate-700">
              <Download size={11} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Stage3 = () => (
  <div className="space-y-4">
    <ul className="space-y-0.5">
      <TimelineItem title="Draft Contract Received" sub="Today · 09:14" />
      <TimelineItem title="Enquiries Raised" sub="12 Total · Today · 09:32" />
      <TimelineItem state="active" title="Management Pack Requested" sub="Today · 09:35" badge="In Progress" />
      <TimelineItem state="pending" title="Awaiting Responses" sub="" badge="In Progress" />
    </ul>
    <div>
      <ChatMessage name="Sarah Mitchell" role="Buyer Solicitor"  time="Today · 09:14"
                   body="Draft contract uploaded."
                   attachment={{ name: "Draft Contract.pdf", size: "1.2 MB" }} />
      <ChatMessage name="David Lee" role="Seller Solicitor" time="Today · 09:32"
                   body="Enquiries raised. Please see list below."
                   attachment={{ name: "Enquiries List (Q1 – Q12).pdf", size: "245 KB" }} />
      <ChatMessage system time="Today · 09:35"
                   body="Enquiries raised by seller solicitor (12 total)." />
      <ChatMessage name="Jack Richmond" role="Agent" time=""
                   body="Thanks both. I'll chase the management pack." />

      <div className="mt-3 rounded-lg flex items-center gap-2 pl-3 pr-1.5 py-1.5"
           style={{ background: "#F8FAFC", border: `1px solid ${T.innerBorder}` }}>
        <span className="text-[11px] flex-1" style={{ color: T.textSubtle }}>Type a message…</span>
        <button type="button" className="h-7 px-3 rounded-md text-[10.5px] font-bold text-white"
                style={{ background: T.violet }}>
          Send
        </button>
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] font-semibold flex-wrap" style={{ color: T.textMuted }}>
        <span className="inline-flex items-center gap-1"><Paperclip size={10} /> Attach</span>
        <span className="inline-flex items-center gap-1"><FileText size={10} /> Request</span>
        <span className="inline-flex items-center gap-1"><Plus size={10} /> Milestone</span>
        <span className="inline-flex items-center gap-1 ml-auto"><MoreHorizontal size={10} /> More</span>
      </div>
    </div>
  </div>
);

/* ── Stage 4 · Mortgage Progress ─────────────────────────────────────── */
const Stage4 = () => (
  <div className="space-y-4">
    <ul className="space-y-0.5">
      <TimelineItem title="Mortgage Application Submitted" sub="Today · 12:01" />
      <TimelineItem title="Valuation Booked" sub="16 Apr 2026" />
      <TimelineItem title="Mortgage Offer Received" sub="22 Apr 2026" />
      <TimelineItem title="Mortgage Offer Uploaded" sub="22 Apr 2026" />
      <TimelineItem state="active" title="Awaiting Final Enquiries" sub="" badge="In Progress" />
      <TimelineItem state="active" title="Searches in Progress" sub="" badge="In Progress" />
      <TimelineItem state="pending" title="Ready for Exchange" sub="" badge="Pending" />
    </ul>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Activity</p>
      <ul className="space-y-0">
        <ActivityRow initials="TB" color="#A5B4FC" who="Tom Bradley" action="uploaded Mortgage Offer" at="Today · 12:42" />
      </ul>
      <div className="mt-2 inline-flex items-center gap-2 rounded-md px-2.5 py-1.5"
           style={{ background: "#FAFBFD", border: `1px solid ${T.innerBorder}` }}>
        <FileText size={11} className="text-slate-400" />
        <div>
          <p className="text-[10.5px] font-bold" style={{ color: T.textPrimary }}>Mortgage Offer.pdf</p>
          <p className="text-[9.5px]" style={{ color: T.textSubtle }}>1.1 MB</p>
        </div>
      </div>
      <ul className="mt-2 space-y-0">
        <ActivityRow initials="JR" color="#FBBF24" who="Jack Richmond" action='— "Great news! This moves us forward."' at="Today · 12:45" />
      </ul>
    </div>
  </div>
);

/* ── Stage 5 · Exchange Coordination ─────────────────────────────────── */
const ChecklistRow = ({ label, status }) => (
  <li className="flex items-center justify-between py-1">
    <span className="inline-flex items-center gap-2 text-[11px]" style={{ color: T.textPrimary }}>
      {status === "COMPLETED" ? (
        <Check size={11} strokeWidth={3} className="text-emerald-600" />
      ) : (
        <span className="w-2 h-2 rounded-full" style={{ background: T.violet }} />
      )}
      {label}
    </span>
    <span className="text-[9px] font-bold uppercase tracking-[0.10em] px-1.5 h-[16px] inline-flex items-center rounded-full"
          style={
            status === "COMPLETED"
              ? { background: T.greenSoft, color: "#047857", border: `1px solid ${T.greenBorder}` }
              : { background: T.violetSoft, color: T.violet, border: `1px solid ${T.violetBorder}` }
          }>
      {status}
    </span>
  </li>
);

const RiskCard = ({ severity, title, sub, days }) => {
  const palette =
    severity === "HIGH"   ? { bg: T.roseSoft,  fg: "#B91C1C", border: T.roseBorder,  dotColor: T.rose,  Icon: AlertTriangle }
                          : { bg: T.amberSoft, fg: "#B45309", border: T.amberBorder, dotColor: T.amber, Icon: AlertTriangle };
  const Icon = palette.Icon;
  return (
    <div className="rounded-lg p-2.5 mb-1.5"
         style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
      <div className="flex items-start gap-2">
        <Icon size={11} strokeWidth={2.4} style={{ color: palette.dotColor }} className="mt-[2px] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-bold leading-tight" style={{ color: palette.fg }}>{title}</p>
            <span className="text-[8.5px] font-bold uppercase tracking-[0.10em] px-1.5 h-[15px] inline-flex items-center rounded-full"
                  style={{ background: "#FFFFFF", color: palette.fg, border: `1px solid ${palette.border}` }}>
              {severity}
            </span>
          </div>
          <p className="text-[10px] mt-0.5" style={{ color: palette.fg, opacity: 0.85 }}>{sub}</p>
          {days && <p className="text-[9.5px] mt-0.5 font-semibold" style={{ color: palette.fg, opacity: 0.8 }}>{days}</p>}
        </div>
      </div>
    </div>
  );
};

const Stage5 = () => (
  <div className="space-y-4">
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Readiness Checklist</p>
      <ul className="space-y-0">
        <ChecklistRow label="AML Completed" status="COMPLETED" />
        <ChecklistRow label="ID Verified" status="COMPLETED" />
        <ChecklistRow label="Proof of Funds" status="COMPLETED" />
        <ChecklistRow label="TA6 Received" status="COMPLETED" />
        <ChecklistRow label="TA10 Received" status="COMPLETED" />
        <ChecklistRow label="Draft Contract Received" status="COMPLETED" />
        <ChecklistRow label="Enquiries Cleared" status="IN PROGRESS" />
        <ChecklistRow label="Searches Received" status="IN PROGRESS" />
        <ChecklistRow label="Mortgage Offer" status="COMPLETED" />
        <ChecklistRow label="Deposit Confirmed" status="IN PROGRESS" />
      </ul>
    </div>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Active Risks</p>
      <RiskCard severity="MEDIUM" title="Search Delay Risk"   sub="Local search delayed" days="3 days" />
      <RiskCard severity="HIGH"   title="Enquiries Outstanding" sub="3 replies overdue" days="2 days" />
      <RiskCard severity="MEDIUM" title="Management Pack Delay" sub="Awaiting final leases" days="2 days" />
      <button type="button"
              className="mt-1.5 w-full h-9 rounded-lg text-[11px] font-bold inline-flex items-center justify-center gap-1.5"
              style={{ background: "#FFFFFF", color: T.textPrimary, border: `1px solid ${T.innerBorder}` }}>
        View All Risks <ArrowRight size={11} />
      </button>
    </div>
  </div>
);

/* ── Stage 6 · Ready for Exchange ────────────────────────────────────── */
const NextStepItem = ({ state, label }) => {
  const dot =
    state === "active" ? T.violet : state === "pending" ? "#CBD5E1" : T.green;
  return (
    <li className="flex items-center gap-2 py-1.5">
      <span
        className="w-3 h-3 rounded-full inline-flex items-center justify-center shrink-0"
        style={{ background: state === "active" ? "#FFFFFF" : dot, border: `1.5px solid ${dot}` }}
      >
        {state === "active" && <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />}
      </span>
      <span className="text-[11px]" style={{ color: state === "pending" ? T.textMuted : T.textPrimary, fontWeight: state === "active" ? 700 : 600 }}>
        {label}
      </span>
    </li>
  );
};

const Stage6 = () => (
  <div className="space-y-4">
    <div className="flex flex-col items-center text-center py-2">
      <span
        className="w-16 h-16 rounded-full inline-flex items-center justify-center mb-3"
        style={{ background: T.greenSoft, border: `2px solid ${T.greenBorder}` }}
      >
        <Check size={28} strokeWidth={3} className="text-emerald-600" />
      </span>
      <p className="text-[16px] font-bold" style={{ color: T.textPrimary }}>Ready for Exchange</p>
      <p className="text-[11px] mt-1 max-w-[260px]" style={{ color: T.textMuted }}>
        All critical milestones completed.<br />You're ready to exchange contracts.
      </p>
    </div>

    <div className="grid grid-cols-3 gap-2">
      {[
        ["Deal Health",        "Excellent", T.green],
        ["Exchange Probability", "94%",     T.violet],
        ["Momentum",           "Strong ↗", T.green],
      ].map(([k, v, color]) => (
        <div key={k} className="rounded-lg p-2.5 text-center"
             style={{ background: "#FAFBFD", border: `1px solid ${T.innerBorder}` }}>
          <p className="text-[9px] font-bold uppercase tracking-[0.10em]" style={{ color: T.textSubtle }}>{k}</p>
          <p className="font-bold mt-1 text-[14px] tabular-nums" style={{ color }}>{v}</p>
        </div>
      ))}
    </div>

    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: T.textSubtle }}>Next Steps</p>
      <ul>
        <NextStepItem state="active" label="Agree Exchange Date" />
        <NextStepItem state="pending" label="Complete Final Due Diligence" />
        <NextStepItem state="pending" label="Exchange Contracts" />
        <NextStepItem state="pending" label="Prepare Completion" />
        <NextStepItem state="pending" label="Completion Day" />
      </ul>
    </div>

    <div className="space-y-2">
      <button type="button"
              className="w-full h-10 rounded-xl text-white text-[12.5px] font-bold inline-flex items-center justify-center gap-2"
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)",
                boxShadow: "0 14px 28px -10px rgba(99,102,241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
              }}>
        Notify All Parties
      </button>
      <button type="button"
              className="w-full h-10 rounded-xl text-[12.5px] font-bold inline-flex items-center justify-center gap-2"
              style={{ background: "#FFFFFF", color: T.textPrimary, border: `1px solid ${T.innerBorder}` }}>
        Book Exchange Date
      </button>
    </div>
  </div>
);

/* ── "What Makes This Powerful" footer strip ─────────────────────────── */
const POWERFUL = [
  { Icon: Eye,           title: "Complete Visibility", sub: "Everything in one place" },
  { Icon: TrendingUp,    title: "Real-Time Updates",   sub: "Automatic and live" },
  { Icon: Handshake,     title: "Accountability",      sub: "Clear ownership" },
  { Icon: AlertTriangle, title: "Risk Intelligence",   sub: "Spot issues early" },
  { Icon: Smartphone,    title: "Mobile First",        sub: "Work from anywhere" },
  { Icon: ShieldCheck,   title: "Faster Exchanges",    sub: "Close more deals" },
];

const PowerfulStrip = () => (
  <div
    className="rounded-2xl p-5"
    style={{ background: T.cardBg, border: `1px solid ${T.cardBorder}`, boxShadow: T.cardShadow }}
    data-testid="gp-powerful"
  >
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-5 gap-y-4 items-start">
      <p className="text-[12.5px] font-bold col-span-2 md:col-span-3 lg:col-span-1" style={{ color: T.textPrimary }}>
        What Makes This Powerful
      </p>
      {POWERFUL.map(({ Icon, title, sub }) => (
        <div key={title} className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0"
                style={{ background: T.violetSoft, color: T.violet, border: `1px solid ${T.violetBorder}` }}>
            <Icon size={14} strokeWidth={2.2} />
          </span>
          <div>
            <p className="text-[11.5px] font-bold leading-tight" style={{ color: T.textPrimary }}>{title}</p>
            <p className="text-[10px] mt-0.5" style={{ color: T.textMuted }}>{sub}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ═════════════════════════════════════════════════════════════════════
   Public component — the Golden Path tab content
   ──────────────────────────────────────────────────────────────────── */
export const GoldenPath = ({ onBack }) => (
  <div data-testid="dealroom-golden-path" className="min-h-full" style={{ background: T.surfaceTint, margin: -16, padding: 24 }}>
    {/* Header bar */}
    <header className="flex items-center justify-between gap-4 flex-wrap mb-6">
      <button
        type="button"
        onClick={onBack}
        className="h-10 px-4 rounded-xl text-[12px] font-semibold inline-flex items-center gap-2 transition-colors"
        style={{ background: "#FFFFFF", color: T.textPrimary, border: `1px solid ${T.innerBorder}`, boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
        data-testid="gp-back"
      >
        <ArrowLeft size={13} /> Back to Deal Rooms
      </button>
      <div className="text-center flex-1 min-w-[280px]">
        <h1 className="font-display text-[20px] lg:text-[24px] font-bold tracking-[-0.018em]" style={{ color: T.textPrimary }}>
          THE GOLDEN PATH: <span style={{ background: "linear-gradient(135deg, #6D28D9, #4F46E5)", WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent" }}>A Complete Transaction</span> from Offer Accepted to Exchange
        </h1>
        <p className="text-[11.5px] mt-1.5" style={{ color: T.textMuted }}>
          See how ChainFlow OS coordinates every party, every update, every step — all in one place.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          className="h-10 px-3.5 rounded-xl text-[12px] font-semibold inline-flex items-center gap-2"
          style={{ background: "#FFFFFF", color: T.textPrimary, border: `1px solid ${T.innerBorder}` }}
          data-testid="gp-share"
        >
          <Share2 size={13} /> Share Deal Room
        </button>
        <button type="button"
                className="relative w-10 h-10 rounded-xl inline-flex items-center justify-center"
                style={{ background: "#FFFFFF", border: `1px solid ${T.innerBorder}` }}>
          <Bell size={14} className="text-slate-700" />
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white inline-flex items-center justify-center"
                style={{ background: T.rose }}>3</span>
        </button>
        <button type="button"
                className="w-10 h-10 rounded-xl inline-flex items-center justify-center"
                style={{ background: "#FFFFFF", border: `1px solid ${T.innerBorder}` }}>
          <MoreHorizontal size={14} className="text-slate-700" />
        </button>
      </div>
    </header>

    {/* 6 stage cards — 3 columns desktop, 2 tablet, 1 mobile */}
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-6">
      <StageCard n={1} title="Offer Accepted" sub="Deal created instantly. All parties invited." state="complete"
                 tabs={[{ label: "Timeline", active: true }, { label: "Overview" }, { label: "Actions", count: 3 }, { label: "Documents" }, { label: "Participants", count: 6 }]}
                 caption="System creates the deal, timeline and invites all parties automatically.">
        <Stage1 />
      </StageCard>

      <StageCard n={2} title="AML + Documents" sub="Buyers and sellers upload required documents." state="in_progress"
                 tabs={[{ label: "Timeline" }, { label: "Overview" }, { label: "Actions", count: 4 }, { label: "Documents", active: true }, { label: "Participants", count: 6 }]}
                 caption="Documents are tracked, verified and updated in real-time.">
        <Stage2 />
      </StageCard>

      <StageCard n={3} title="Solicitor Coordination" sub="Solicitors collaborate, raise enquiries and share updates." state="in_progress"
                 tabs={[{ label: "Timeline", active: true }, { label: "Overview" }, { label: "Actions", count: 5 }, { label: "Documents" }, { label: "Participants", count: 6 }]}
                 caption="All communication in one place. Enquiries tracked and visible.">
        <Stage3 />
      </StageCard>

      <StageCard n={4} title="Mortgage Progress" sub="Broker updates mortgage status and uploads offer." state="in_progress"
                 tabs={[{ label: "Timeline", active: true }, { label: "Overview" }, { label: "Actions", count: 3 }, { label: "Documents" }, { label: "Participants", count: 6 }]}
                 caption="Mortgage milestones update automatically and impact deal health.">
        <Stage4 />
      </StageCard>

      <StageCard n={5} title="Exchange Coordination" sub="System tracks readiness, highlights blockers and risks." state="at_risk"
                 tabs={[{ label: "Overview", active: true }, { label: "Timeline" }, { label: "Actions", count: 2 }, { label: "Documents" }, { label: "Participants", count: 6 }]}
                 caption="Deal health and risks are calculated in real-time.">
        <Stage5 />
      </StageCard>

      <StageCard n={6} title="Ready for Exchange" sub="All parties aligned. Exchange ready." state="ready"
                 tabs={[{ label: "Overview", active: true }, { label: "Timeline" }, { label: "Actions" }, { label: "Documents" }, { label: "Participants", count: 6 }]}
                 caption="The system gives agents confidence to move to exchange.">
        <Stage6 />
      </StageCard>
    </div>

    <PowerfulStrip />
  </div>
);

export default GoldenPath;
